/**
 * Convert a datetime-local input value (naive local ISO string, e.g. "2026-06-08T10:00")
 * to a UTC ISO 8601 string (e.g. "2026-06-08T09:00:00.000Z") for sending to the backend.
 * Returns null for empty / falsy input.
 */
export function localToUTC(localDatetimeStr) {
  if (!localDatetimeStr) return null;
  return new Date(localDatetimeStr).toISOString();
}

/**
 * Parse a UTC ISO 8601 datetime string returned from the backend (with or without offset)
 * and return a JavaScript Date object.
 */
export function parseUTC(utcStr) {
  if (!utcStr) return null;
  const normalised = /[Zz]$|[+\-]\d{2}:?\d{2}$/.test(utcStr.trim())
    ? utcStr
    : utcStr + "Z";
  const dt = new Date(normalised);
  return isNaN(dt.getTime()) ? null : dt;
}

/**
 * Convert a UTC ISO 8601 string returned from the backend into the
 * "YYYY-MM-DDTHH:mm" format expected by datetime-local inputs, expressed
 * in the browser's local timezone so the teacher sees their own time.
 * Returns "" for empty / falsy input.
 */
export function utcToDatetimeLocal(utcStr) {
  if (!utcStr) return "";
  // Append "Z" when no timezone offset is present so the browser treats the
  // value as UTC instead of local time (same normalisation as formatUtcDatetime).
  const normalised = /[Zz]$|[+\-]\d{2}:?\d{2}$/.test(utcStr.trim())
    ? utcStr
    : utcStr + "Z";
  const dt = new Date(normalised);
  if (isNaN(dt.getTime())) return "";
  // Build a local-time string in "YYYY-MM-DDTHH:mm" format for datetime-local inputs
  const pad = (n) => String(n).padStart(2, "0");
  const year = dt.getFullYear();
  const month = pad(dt.getMonth() + 1);
  const day = pad(dt.getDate());
  const hours = pad(dt.getHours());
  const minutes = pad(dt.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Format a UTC datetime string returned by the backend for display in the
 * user's local timezone.
 *
 * The backend stores naive UTC datetimes without a timezone suffix (e.g.
 * "2026-06-08T14:30:45"). Without a suffix, browsers treat the string as
 * *local* time — which is wrong. This function appends "Z" when no offset is
 * present so the value is correctly interpreted as UTC before being converted
 * to the user's local timezone for display.
 *
 * @param {string} utcStr - ISO 8601 string from the backend, e.g. "2026-06-08T14:30:45"
 * @returns {string} Human-readable local datetime, e.g. "08 Jun 2026, 2:30 pm"
 */
export function formatUtcDatetime(utcStr) {
  if (!utcStr) return "—";
  const normalised = /[Zz]$|[+\-]\d{2}:?\d{2}$/.test(utcStr.trim())
    ? utcStr
    : utcStr + "Z";
  const dt = new Date(normalised);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a date string or Date object into ordinal format, e.g. "11th June 2026".
 *
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Formatted ordinal date string, e.g. "11th June 2026"
 */
export function formatDateOrdinal(dateInput) {
  if (!dateInput) return "—";
  const dt = dateInput instanceof Date ? dateInput : parseUTC(typeof dateInput === "string" ? dateInput : "") || new Date(dateInput);
  if (!dt || isNaN(dt.getTime())) return "—";
  const day = dt.getDate();
  const getSuffix = (d) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:  return "st";
      case 2:  return "nd";
      case 3:  return "rd";
      default: return "th";
    }
  };
  const monthName = dt.toLocaleString("en-GB", { month: "long" });
  const year = dt.getFullYear();
  return `${day}${getSuffix(day)} ${monthName} ${year}`;
}

